<div class="drcf-container">
  <h2>Data Release Consent Form (DRCF)</h2>
  
  @if (isLoadingEnvelopes || isCreatingEnvelope || isVoidingEnvelope || isDownloadingDocument || isOpeningDocument || isDownloadingTemplate) {
    <div class="loading-overlay">
      <div class="loading-content">
        <mat-spinner diameter="60"></mat-spinner>
        <p class="loading-message">
          @if (isCreatingEnvelope) {
            Creating envelope...
          } @else if (isVoidingEnvelope) {
            Voiding document...
          } @else if (isDownloadingDocument) {
            Downloading document...
          } @else if (isDownloadingTemplate) {
            Downloading template...
          } @else if (isOpeningDocument) {
            Opening document...
          } @else if (isLoadingEnvelopes) {
            Loading documents...
          }
        </p>
      </div>
    </div>
  }

  @if (isDrcfSigningEnabled) {
    <div class="drcf-info">
      <mat-icon>info</mat-icon>
      <p>A DRCF is required to be signed before you submit your measures.</p>
    </div>
  } @else {
    <div class="drcf-info">
      <mat-icon>info</mat-icon>
      <p>DRCF signing is temporarily unavailable for this performance year. Submission is on hold until it reopens. You can still download the DRCF template below.</p>
    </div>

    <div class="top-actions">
      <button
        mat-raised-button
        color="primary"
        (click)="downloadDrcfTemplate()"
        [disabled]="isDownloadingTemplate"
        class="action-button-primary">
        @if (isDownloadingTemplate) {
          <mat-spinner diameter="20"></mat-spinner>
          Downloading...
        } @else {
          <ng-container>
            <mat-icon>description</mat-icon>
            Download DRCF Template
          </ng-container>
        }
      </button>
    </div>
  }

  @if (showTabs) {
    <div class="reporting-tabs">
      <button
        class="tab-btn"
        [class.active]="currentTab === 'traditional'"
        (click)="onTabChange('traditional')"
      >
        Traditional MIPS
      </button>
      <button
        class="tab-btn"
        [class.active]="currentTab === 'mvp'"
        (click)="onTabChange('mvp')"
      >
        MVP
      </button>
    </div>
  }

  @if (isDrcfSigningEnabled) {
  <div class="top-actions">
    <div class="create-buttons">
      @if (!isReadOnly && !isLoadingEnvelopes && canSendNewAgreement) {
        <button
          mat-raised-button
          color="primary"
          (click)="createDrcfEnvelope()"
          [disabled]="isCreatingEnvelope"
          class="action-button-primary">
          @if (isCreatingEnvelope) {
            <mat-spinner diameter="20"></mat-spinner>
            Creating...
          } @else {
            <ng-container>
              @if (hasCompletedDocument || allDocumentsVoided) {
                Create New DRCF (Sign Now)
              } @else {
                Create DRCF (Sign Now)
              }
            </ng-container>
          }
        </button>

        <button
          mat-raised-button
          color="accent"
          (click)="createDrcfEnvelopeWithEmail()"
          [disabled]="isCreatingEnvelope"
          class="action-button-secondary">
          @if (isCreatingEnvelope) {
            <mat-spinner diameter="20"></mat-spinner>
            Creating...
          } @else {
            <ng-container>
              Send via Email
            </ng-container>
          }
        </button>
      }

      @if (!isReadOnly && !isLoadingEnvelopes && mustVoidFirst) {
        <div class="warning-inline">
          <mat-icon>warning</mat-icon>
          <span>Please void the pending DRCF before creating a new one.</span>
        </div>
      }
    </div>

    <button 
      mat-raised-button
      (click)="refreshDocuments()"
      [disabled]="isLoadingEnvelopes"
      class="refresh-button-top">
      @if (isLoadingEnvelopes) {
        <mat-spinner diameter="20"></mat-spinner>
        Refreshing...
      } @else {
        <ng-container>
          <mat-icon>refresh</mat-icon>
          Refresh Status
        </ng-container>
      }
    </button>
  </div>

  @if (hasCompletedDocument && canSendNewAgreement) {
    <div class="info-message">
      <mat-icon>info</mat-icon>
      <p>You have a signed DRCF. Creating a new DRCF will permanently delete the existing one.</p>
    </div>
  }
  }

  @if (isLoadingEnvelopes) {
    <div class="loading-container">
      <mat-spinner diameter="40"></mat-spinner>
      <p>Loading DRCF documents...</p>
    </div>
  }

  @if (!isLoadingEnvelopes && drcfDocuments.length > 0) {
    <div class="drcf-table-container">
      <table class="drcf-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th>Completed Date</th>
            <th>Signatory</th>
            <th>Download</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          @for (doc of drcfDocuments; track doc.envelopeId) {
            <tr>
              <td>DRCF Agreement {{ performanceYear }}</td>
              <td>
                <span 
                  class="status-badge"
                  [ngClass]="'status-' + doc.status">
                  {{ getStatusText(doc.status) }}
                </span>
              </td>
              <td>{{ doc.signedOn ? (doc.signedOn | date:'MM/dd/yyyy') : '-' }}</td>
              <td>
                @if ((doc.status === 'sent' || doc.status === 'delivered') && doc.assignedTo) {
                  <div class="assigned-info">
                    <div class="assigned-name-row">
                      <span class="assigned-name">{{ doc.assignedTo }}</span>
                      @if (doc.isEmbeddedSigning) {
                        <mat-icon
                          class="embedded-icon"
                          matTooltip="Must be signed by this person in the application">
                          person_pin
                        </mat-icon>
                      }
                    </div>
                    @if (doc.assignedToEmail) {
                      <span class="assigned-email">({{ doc.assignedToEmail }})</span>
                    }
                  </div>
                } @else if (doc.status === 'completed' && doc.signedBy) {
                  {{ doc.signedBy }}
                } @else {
                  -
                }
              </td>
              <td>
                @if (canDownloadDocument(doc)) {
                  <button 
                    mat-icon-button 
                    (click)="downloadDocument(doc.envelopeId)"
                    matTooltip="Download DRCF"
                    [disabled]="isDownloadingDocument"
                    color="primary">
                    <mat-icon>description</mat-icon>
                  </button>
                } @else if (isDocumentProcessing(doc)) {
                  <mat-icon 
                    matTooltip="Document is being processed. Please refresh in a moment."
                    class="processing-icon">
                    hourglass_empty
                  </mat-icon>
                } @else {
                  <span class="not-available">-</span>
                }
              </td>
              <td>
                @if (isDrcfSigningEnabled && !isReadOnly && (doc.status === 'sent' || doc.status === 'delivered')) {
                  <div class="action-buttons-cell">
                    <button
                      mat-raised-button
                      color="primary"
                      (click)="openDocument(doc.envelopeId)"
                      [disabled]="isOpeningDocument"
                      class="sign-button">
                      Sign Now
                    </button>
                    <button
                      mat-button
                      color="warn"
                      (click)="voidDocument(doc.envelopeId)"
                      [disabled]="isVoidingEnvelope"
                      class="void-button">
                      Void
                    </button>
                  </div>
                } @else if (doc.status === 'completed' && !isReadOnly) {
                  <button
                    mat-button
                    color="primary"
                    (click)="openDocument(doc.envelopeId)"
                    [disabled]="isOpeningDocument"
                    class="view-button">
                    View
                  </button>
                } @else {
                  <span class="not-available">-</span>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }

  @if (!isLoadingEnvelopes && drcfDocuments.length === 0) {
    <div class="no-documents">
      <mat-icon class="no-docs-icon">description</mat-icon>
      <p class="no-documents-text">No DRCF documents found.</p>
      @if (isDrcfSigningEnabled) {
        <p class="no-documents-subtext">Create a DRCF using the buttons above.</p>
      }
    </div>
  }
</div>